/*
  Mega menu v6 — hover to open, click to navigate, drag-proof.

  v5: every top-level link opens a panel on hover, not just the ones with a
  submenu. Links that have children keep their own panel ("into the mix").
  Links without children share the single auto-built card grid rendered as
  #MegaMenu-Shared, so the "shop" submenu can be dropped from the Shopify
  navigation without losing the card grid — and "shop all" becomes a plain
  one-tap link on mobile.

  ROOT CAUSE FIX for the "frozen page" bug:
  A press-and-drag on nav text or a card image starts a native browser
  drag/selection. Native drags suppress all mouse/pointer events tab-wide.
  The v1-v3 close timer then mutated the DOM (collapsing the <details>)
  mid-drag, wedging Chrome's drag session and leaving the tab with mouse
  events permanently suppressed ("frozen in whatever state at unclick").

  v4 defenses:
  1. dragstart is cancelled for the ENTIRE inline nav (capture phase) —
     covers summaries, links, card images, and selected text alike.
  2. All images in the nav get draggable=false (Firefox coverage).
  3. The close timer is drag-aware: if a native drag is active anywhere,
     closing is deferred until the drag ends. No DOM mutation mid-drag.
  4. Recovery nets kept from v2: outside-click, Escape, window blur.

  v7 (2026-10-16, Joseph): an open panel folds away when the shopper starts
  scrolling. The first wheel or trackpad travel past a small threshold, or any
  page scroll, fades and lifts the panel (the mega-menu--closing state in
  component-mega-menu.css) and then collapses it, instead of leaving it open
  over a page that scrolls behind it. A wheel over a panel that scrolls on its
  own is scrolling the panel, not the page, and is left alone. Hovering the nav
  word again mid-fade brings the panel back. The drag rule still holds: a fold
  that lands mid-drag waits for the drag to settle like any other close.
  The page moving under a resting pointer also reads as an "enter" (the sticky
  header sliding back under it on a scroll up), so an enter that arrives while
  the page is still scrolling opens only once the scroll has settled and the
  pointer is still on the word.
  Bridge: the header's bottom padding sits between the nav word and the panel,
  so a slow move down left the word (close timer) before reaching the panel.
  On each open the gap is measured and set on the nav as --mega-menu-bridge;
  component-mega-menu.css draws an invisible strip of that height under the
  word, inside its hover area. The strip is hover area only: it shows the
  arrow cursor, and a click on it neither navigates nor toggles.
*/

(function () {
  const HOVER_MQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DESKTOP_MQ = window.matchMedia('(min-width: 990px)');
  const CLOSE_DELAY_MS = 150;
  const FOLD_MS = 300;          // matches the transition on .mega-menu--closing (component-mega-menu.css)
  const SCROLL_THRESHOLD_PX = 8; // wheel travel or page movement before a panel folds: a resting trackpad can emit a pixel or two
  const SETTLE_MS = 200;        // an enter during a scroll opens only after the page has been still this long

  const PANEL_SELECTOR = '.header__inline-menu details.mega-menu';
  const ACTIVE_CARD = 'mega-menu__card--active';
  const CLOSING = 'mega-menu--closing';

  const hoverMode = () => HOVER_MQ.matches && DESKTOP_MQ.matches;

  // --- Drag session tracking (document-wide) ---
  let dragActive = false;
  let pendingClosers = [];

  function onDragSettled() {
    dragActive = false;
    // Run any close that was deferred during the drag
    pendingClosers.forEach((fn) => fn());
    pendingClosers = [];
  }

  document.addEventListener('dragstart', () => { dragActive = true; }, true);
  document.addEventListener('dragend', onDragSettled, true);
  document.addEventListener('drop', onDragSettled, true);

  // Smooth closes in flight: details -> timer that collapses it after the fade
  const foldTimers = new WeakMap();

  function closeMenu(details, smooth = false) {
    if (dragActive) {
      // NEVER mutate the DOM mid-drag — defer until the drag settles.
      pendingClosers.push(() => closeMenu(details, smooth));
      return;
    }
    if (smooth && details.hasAttribute('open') && !details.classList.contains(CLOSING)) {
      // Fade and lift first (CSS), collapse once that has played.
      details.classList.add(CLOSING);
      foldTimers.set(details, setTimeout(() => collapse(details), FOLD_MS));
      return;
    }
    collapse(details);
  }

  function collapse(details) {
    clearTimeout(foldTimers.get(details));
    details.classList.remove(CLOSING);
    details.removeAttribute('open');
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
    details
      .querySelectorAll('.' + ACTIVE_CARD)
      .forEach((card) => card.classList.remove(ACTIVE_CARD));
    syncNavOpenClass(details.closest('.header__inline-menu'));
  }

  // --- Bridge: the gap between the nav row and the panel joins the hover area ---
  const NAV_OPEN = 'mega-menu-open';

  function syncNavOpenClass(nav) {
    if (!nav) return;
    nav.classList.toggle(NAV_OPEN, !!nav.querySelector('details.mega-menu[open]'));
  }

  // True when a click landed on the bridge strip below the word's own box.
  function onBridge(trigger, event) {
    return event.clientY >= trigger.getBoundingClientRect().bottom - 0.5;
  }

  // A click on the strip is a click on nothing: no navigation, no toggle.
  function ignoreBridgeClicks(trigger) {
    trigger.addEventListener('click', (event) => {
      if (!hoverMode() || !onBridge(trigger, event)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    });
  }

  // Measured from the nav row rather than the trigger: hovering the panel
  // itself re-opens it (the shared panel) and must not shrink the bridge.
  function sizeBridge(details) {
    const nav = details.closest('.header__inline-menu');
    const content = details.querySelector('.mega-menu__content');
    if (!nav || !content) return;
    const row = nav.querySelector('.list-menu--inline') || nav;
    const gap = content.getBoundingClientRect().top - row.getBoundingClientRect().bottom;
    nav.style.setProperty('--mega-menu-bridge', Math.max(0, Math.ceil(gap)) + 'px');
    syncNavOpenClass(nav);
  }

  // A fold in progress is cancelled by reopening: the panel comes straight back.
  function cancelFold(details) {
    clearTimeout(foldTimers.get(details));
    details.classList.remove(CLOSING);
  }

  function closeAll(except) {
    document.querySelectorAll(PANEL_SELECTOR + '[open]').forEach((details) => {
      if (details !== except) closeMenu(details);
    });
  }

  // --- Scroll-away (v7) ---
  let wheelTravel = 0;   // wheel travel since the panel opened
  let scrollAtOpen = 0;  // page position when it opened
  let lastScrollAt = 0;  // when the page last moved

  function openPanels() {
    return Array.from(document.querySelectorAll(PANEL_SELECTOR + '[open]:not(.' + CLOSING + ')'));
  }

  function foldOpenPanels() {
    const panels = openPanels();
    if (!panels.length) return;
    wheelTravel = 0;
    panels.forEach((details) => {
      clearTimeout(closeTimers.get(details));
      closeMenu(details, true);
    });
  }

  function onWheel(event) {
    if (!hoverMode() || !openPanels().length) return;
    // Scrolling a panel that has its own scrollbar scrolls the panel, not the page.
    const content = event.target && event.target.closest && event.target.closest('.mega-menu__content');
    if (content && content.scrollHeight > content.clientHeight + 1) return;
    wheelTravel += Math.abs(event.deltaY) + Math.abs(event.deltaX);
    if (wheelTravel < SCROLL_THRESHOLD_PX) return;
    foldOpenPanels();
  }

  function onScroll() {
    lastScrollAt = performance.now();
    if (!hoverMode()) return;
    if (Math.abs(window.scrollY - scrollAtOpen) < SCROLL_THRESHOLD_PX) return;
    foldOpenPanels();
  }

  function pageStill() {
    return performance.now() - lastScrollAt >= SETTLE_MS;
  }

  // Bind `trigger` so that hovering it opens `details` and leaving it closes
  // that panel after a short grace period. Several triggers may share a panel.
  const closeTimers = new WeakMap();
  const settleTimers = new WeakMap();

  function openFromHover(details, summary) {
    clearTimeout(closeTimers.get(details));
    cancelFold(details);
    wheelTravel = 0;
    scrollAtOpen = window.scrollY;
    closeAll(details);
    details.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');
    sizeBridge(details);
  }

  function hoverToOpen(trigger, details) {
    const summary = details.querySelector('summary');

    // An enter that arrives while the page is scrolling is the page moving
    // under a resting pointer, not a hover. Open once the page has been still
    // for a moment and the pointer is still on the trigger.
    const openWhenSettled = () => {
      clearTimeout(settleTimers.get(trigger));
      if (!trigger.matches(':hover')) return;
      if (pageStill()) {
        openFromHover(details, summary);
        return;
      }
      settleTimers.set(trigger, setTimeout(openWhenSettled, SETTLE_MS));
    };

    trigger.addEventListener('pointerenter', () => {
      if (!hoverMode()) return;
      clearTimeout(settleTimers.get(trigger));
      if (pageStill()) {
        openFromHover(details, summary);
      } else {
        settleTimers.set(trigger, setTimeout(openWhenSettled, SETTLE_MS));
      }
    });

    trigger.addEventListener('pointerleave', () => {
      if (!hoverMode()) return;
      clearTimeout(settleTimers.get(trigger));
      // A folding panel stops taking the pointer, which reads as a leave: let
      // the fold finish on its own clock rather than snapping it shut.
      if (details.classList.contains(CLOSING)) return;
      clearTimeout(closeTimers.get(details));
      closeTimers.set(
        details,
        setTimeout(() => closeMenu(details), CLOSE_DELAY_MS)
      );
    });
  }

  function initMegaMenuHover() {
    const nav = document.querySelector('.header__inline-menu');
    if (!nav) return;

    // 1. No drag may ever begin from the nav — text, links, or images.
    nav.addEventListener('dragstart', (event) => event.preventDefault(), true);

    // 2. Belt-and-suspenders for Firefox: mark nav images non-draggable.
    nav.querySelectorAll('img').forEach((img) => (img.draggable = false));

    nav.querySelectorAll('header-menu > details').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      const url = summary.dataset.url;

      hoverToOpen(details, details);
      ignoreBridgeClicks(summary); // before the navigating click handler below

      // Click navigates instead of toggling — real mouse clicks only.
      if (url && url !== '#') {
        summary.addEventListener('click', (event) => {
          if (!hoverMode()) return;        // touch devices: stock toggle
          if (event.detail === 0) return;  // keyboard: stock toggle
          if (event.metaKey || event.ctrlKey || event.shiftKey) return; // modifier clicks: stock toggle
          event.preventDefault();
          window.location.href = url;
        });
      }
    });

    // --- Shared panel: links without a submenu all open the same card grid ---
    const shared = nav.querySelector('details.mega-menu--shared');
    if (shared) {
      // Hovering the panel itself keeps it open while the pointer travels
      // from the nav row down into the cards.
      hoverToOpen(shared, shared);

      const cards = Array.from(shared.querySelectorAll('[data-mega-card]'));

      nav.querySelectorAll('a[data-mega-shared]').forEach((link) => {
        // Nav word and card are matched by link URL, so the pairing survives
        // any reordering or renaming of the menu.
        const card = cards.find((c) => c.dataset.megaCard === link.dataset.megaShared);

        if (!card) {
          // A top-level link with no card of its own (a page, say). Hovering it
          // should dismiss whatever is open rather than show an unrelated grid.
          link.addEventListener('pointerenter', () => {
            if (hoverMode()) closeAll();
          });
          return;
        }

        hoverToOpen(link, shared);
        ignoreBridgeClicks(link);

        // Hovering the nav word lights up its card exactly as hovering the
        // card itself does.
        link.addEventListener('pointerenter', () => {
          if (!hoverMode()) return;
          cards.forEach((c) => c.classList.remove(ACTIVE_CARD));
          card.classList.add(ACTIVE_CARD);
        });

        link.addEventListener('pointerleave', () => {
          card.classList.remove(ACTIVE_CARD);
        });
      });
    }

    // --- Recovery nets ---
    document.addEventListener('pointerdown', (event) => {
      const insideOpenMenu = event.target.closest(PANEL_SELECTOR + '[open]');
      // In hover mode the nav row manages its own panels: a press on a word
      // that shares the card grid, or on the strip under it, is not "outside"
      // the open panel even though the word sits beside it in the DOM.
      const onNavRow = hoverMode() && event.target.closest('.header__inline-menu');
      if (!insideOpenMenu && !onNavRow) closeAll();
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    window.addEventListener('blur', () => {
      // A window blur can coincide with an OS-level drag; settle first.
      onDragSettled();
      closeAll();
    });

    // --- Scroll-away: the first wheel travel or page scroll folds the panel ---
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    // The sticky header closes panels on its own (HeaderMenu.close); keep the
    // bridge class honest whichever way a panel opens or closes.
    nav.addEventListener('toggle', () => syncNavOpenClass(nav), true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenuHover);
  } else {
    initMegaMenuHover();
  }
})();
